import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { addToWishlist, removeFromWishlist } from '../lib/api';
import { toast } from 'sonner';

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { user } = useAuth();
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    if (user?.wishlist?.includes(product.id)) setWishlisted(true);
  }, [user, product.id]);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Sign in to add to wishlist'); return; }
    try {
      if (wishlisted) {
        await removeFromWishlist(product.id);
        setWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.id);
        setWishlisted(true);
        toast.success('Added to wishlist');
      }
    } catch { toast.error('Could not update wishlist'); }
  };

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <Link
      to={`/products/${product.slug}`}
      className="product-card bg-white border border-slate-200 rounded-lg overflow-hidden group relative"
      data-testid={`product-card-${product.id}`}
    >
      {/* Discount badge */}
      {discount && (
        <div className="absolute top-2 left-2 z-10 bg-[#EF4444] text-white text-xs font-bold px-2 py-1 rounded">
          -{discount}%
        </div>
      )}

      {/* Deal badge */}
      {product.deal && (
        <div className={`absolute top-2 ${discount ? 'left-16' : 'left-2'} z-10 bg-[#FFD700] text-slate-900 text-xs font-bold px-2 py-1 rounded`}>
          DEAL
        </div>
      )}

      {/* Wishlist button */}
      <button
        onClick={handleWishlist}
        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center shadow transition-colors ${
          wishlisted ? 'bg-red-500 text-white' : 'bg-white/80 text-slate-400 hover:text-red-500'
        }`}
        data-testid={`wishlist-btn-${product.id}`}
      >
        <Heart size={16} className={wishlisted ? 'fill-current' : ''} />
      </button>

      {/* Image */}
      <div className="aspect-square overflow-hidden bg-slate-100">
        <img
          src={product.images?.[0] || '/placeholder.png'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-medium text-slate-900 line-clamp-2 mb-1 group-hover:text-[#003366] transition-colors">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={14}
                className={star <= Math.round(product.rating) ? 'fill-[#FFD700] text-[#FFD700]' : 'text-slate-300'}
              />
            ))}
          </div>
          <span className="text-xs text-slate-500">({product.review_count})</span>
        </div>

        {/* Price */}
        <div className="price-tag mb-3">
          <span className="text-lg font-bold text-[#B12704]">${product.price.toFixed(2)}</span>
          {product.original_price && (
            <span className="ml-2 text-sm text-slate-400 line-through">
              ${product.original_price.toFixed(2)}
            </span>
          )}
        </div>

        {/* Add to cart button */}
        <Button
          onClick={handleAddToCart}
          className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-semibold btn-press"
          data-testid={`add-to-cart-${product.id}`}
        >
          <ShoppingCart size={16} className="mr-2" />
          Add to Cart
        </Button>
      </div>
    </Link>
  );
}
